/**
 * Intake desk. MASTER_PROMPT section 9.
 *
 * Download a template, fill it, and — the part that matters — see a dry-run
 * preview of exactly what will change before anything writes. The plant is
 * being asked to structure data it has never had to structure before, and a
 * silent import that half-works would poison the cost run without anyone
 * noticing.
 *
 * Nothing commits until the preview is clean. There is no partial import.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../../lib/api";
import { downloadFile, uploadIntake } from "../../lib/api2";
import type { IntakePreview, TemplateSpec, User } from "../../lib/types";
import { ops } from "../../lib/api2";
import { SheetHead, SheetToolbar } from "../../components/Sheet";
import { useSheet, type SheetColumn } from "../../lib/sheet";

type SpecColumn = TemplateSpec["columns"][number];
type Issue = IntakePreview["issues"][number];

const SPEC_COLUMNS: SheetColumn<SpecColumn>[] = [
  { key: "name", label: "Column", value: (c) => c.name },
  { key: "description", label: "Description", value: (c) => c.description },
  { key: "required", label: "Required",
    value: (c) => (c.required ? "Required" : "Optional") },
];

const ISSUE_COLUMNS: SheetColumn<Issue>[] = [
  { key: "row", label: "Row", type: "number", numeric: true,
    value: (i) => i.row },
  // Filtering by column is the point: a template rejected on 200 rows is
  // usually one column misunderstood, not 200 separate mistakes.
  { key: "column", label: "Column", value: (i) => i.column },
  { key: "problem", label: "Problem", value: (i) => i.problem },
];

export function IntakeDesk({ user }: { user: User }) {
  const [templates, setTemplates] = useState<TemplateSpec[] | null>(null);
  const [selected, setSelected] = useState<string>("labour");
  const [preview, setPreview] = useState<IntakePreview | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<{
    tone: "positive" | "negative" | "warning";
    text: string;
  } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ops
      .templates()
      .then((t) => {
        setTemplates(t);
        if (t.length && !t.some((x) => x.name === selected)) {
          setSelected(t[0].name);
        }
      })
      .catch((e: ApiError) => setBanner({ tone: "negative", text: e.message }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const spec = templates?.find((t) => t.name === selected);

  const specSheet = useSheet(spec?.columns ?? [], SPEC_COLUMNS);
  const issueSheet = useSheet(preview?.issues ?? [], ISSUE_COLUMNS);

  // The sample's shape is whatever the template produced, so its columns are
  // built from the first row rather than declared. Everything is treated as
  // text: a preview is about recognising your own data, not computing on it.
  const sampleColumns = useMemo<SheetColumn<Record<string, unknown>>[]>(() => {
    const first = preview?.sample?.[0];
    if (!first) return [];
    return Object.keys(first).map((key) => ({
      key,
      label: key.replace(/_/g, " "),
      numeric: typeof first[key] === "number",
      type: typeof first[key] === "number" ? "number" : "text",
      value: (row: Record<string, unknown>) => {
        const raw = row[key];
        if (raw === null || raw === undefined) return null;
        return typeof raw === "number" ? raw : String(raw);
      },
    }));
  }, [preview]);
  const sampleSheet = useSheet(preview?.sample ?? [], sampleColumns);
  // The importers that exist, and the roles the API will actually accept an
  // upload from — MASTERS CREATE. Everyone else can read the templates and
  // download them; showing them an Import button that can only 403 is the
  // screen promising something the server will refuse.
  const hasImporter = selected === "labour" || selected === "meter_reading";
  const mayImport = user.role === "SUPER_ADMIN" || user.role === "ACCOUNTS";
  const uploadable = hasImporter && mayImport;

  async function download() {
    try {
      await downloadFile(
        `/api/intake/templates/${selected}`,
        `${selected}-template.xlsx`
      );
    } catch (err) {
      setBanner({ tone: "negative", text: (err as ApiError).message });
    }
  }

  async function run(mode: "preview" | "commit") {
    if (!file) {
      setBanner({ tone: "warning", text: "Choose a filled template first." });
      return;
    }
    setBusy(true);
    setBanner(null);
    try {
      const result = await uploadIntake(selected, file, mode);
      setPreview(result);
      setBanner(
        mode === "commit"
          ? { tone: "positive", text: `Imported. ${result.summary}` }
          : result.is_clean
            ? { tone: "positive", text: result.summary }
            : { tone: "warning", text: result.summary }
      );
      if (mode === "commit") {
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
      }
    } catch (err) {
      setBanner({ tone: "negative", text: (err as ApiError).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">Data intake</h2>
          <p className="page__sub">
            Download a template, fill it, preview what it would change, then
            import
          </p>
        </div>
        <div className="page__controls">
          <label className="inputgroup">
            <span className="label">Template</span>
            <select
              value={selected}
              onChange={(e) => {
                setSelected(e.target.value);
                setPreview(null);
              }}
            >
              {(templates ?? []).map((t) => (
                <option key={t.name} value={t.name}>
                  {t.title}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      {banner && (
        <div className={`banner banner--${banner.tone}`} role="alert">
          {banner.text}
        </div>
      )}

      {spec && (
        <section className="card">
          <h3 className="section-title">{spec.title}</h3>
          <p className="section-note">{spec.note}</p>

          <div className="tablewrap">
            <SheetToolbar sheet={specSheet} exportName="template-columns" />
            <table className="table">
              <SheetHead sheet={specSheet} />
              <tbody>
                {specSheet.rows.map((c) => (
                  <tr key={c.name}>
                    <td className="mono">{c.name}</td>
                    <td>{c.description || "—"}</td>
                    <td>
                      {c.required ? (
                        <span className="pill">required</span>
                      ) : (
                        <span className="kpi__sub">optional</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="page__footer">
            <span className="entrytotals__sub">
              The template carries its guidance above the header and a worked
              example on a second sheet, so everything below the header is data.
            </span>
            <button className="btn btn--ghost btn--auto" onClick={download}>
              Download template
            </button>
          </div>
        </section>
      )}

      {uploadable ? (
        <section className="card">
          <h3 className="section-title">Upload</h3>
          <p className="section-note">
            Preview first. Nothing is written until the preview comes back
            clean, and there is no partial import.
          </p>
          <div className="page__footer">
            <label className="inputgroup inputgroup--grow">
              <span className="label">Filled template</span>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx"
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null);
                  setPreview(null);
                }}
              />
            </label>
            <button
              className="btn btn--ghost btn--auto"
              onClick={() => run("preview")}
              disabled={busy || !file}
            >
              {busy ? <span className="spinner" /> : null}
              Preview
            </button>
            <button
              className="btn btn--auto"
              onClick={() => run("commit")}
              disabled={busy || !file || !preview?.is_clean}
              title={
                preview?.is_clean
                  ? "Import the file"
                  : "Preview must come back clean before importing"
              }
            >
              Import
            </button>
          </div>
        </section>
      ) : !hasImporter ? (
        <div className="banner banner--warning">
          This template can be downloaded and filled, but its importer has not
          been built yet. Labour and meter readings import today; the meter
          master and electricity bill are next.
        </div>
      ) : (
        <div className="banner banner--warning">
          This template imports, but uploading it is for accounts or a super
          admin. Download it, fill it, and hand it to them.
        </div>
      )}

      {preview && (
        <section className="card">
          <h3 className="section-title">
            {preview.is_clean ? "Ready to import" : "Problems found"}
          </h3>
          <div className="kpirow" style={{ marginTop: "var(--sp-4)" }}>
            <div className="kpi">
              <span className="label">Rows read</span>
              <span className="kpi-value num">{preview.rows_read}</span>
            </div>
            <div className="kpi">
              <span className="label">New</span>
              <span className="kpi-value num">{preview.to_create}</span>
            </div>
            <div className="kpi">
              <span className="label">Updated</span>
              <span className="kpi-value num">{preview.to_update}</span>
            </div>
            <div className="kpi">
              <span className="label">Problems</span>
              <span
                className={`kpi-value num${preview.issues.length ? " kpi-value--warn" : ""}`}
              >
                {preview.issues.length}
              </span>
            </div>
          </div>

          {preview.issues.length > 0 && (
            <div className="tablewrap">
              <SheetToolbar sheet={issueSheet} exportName="intake-issues" />
              <table className="table">
                <SheetHead sheet={issueSheet} />
                <tbody>
                  {issueSheet.rows.map((i, index) => (
                    <tr key={`${i.row}-${i.column}-${index}`}>
                      <td className="num mono">{i.row}</td>
                      <td className="mono">{i.column}</td>
                      <td>{i.problem}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview.sample.length > 0 && (
            <>
              <h3 className="section-title" style={{ marginTop: "var(--sp-5)" }}>
                Sample of what will change
              </h3>
              <div className="tablewrap">
                <SheetToolbar sheet={sampleSheet} exportName="intake-sample" />
                <table className="table">
                  <SheetHead sheet={sampleSheet} />
                  <tbody>
                    {sampleSheet.rows.map((row, index) => (
                      <tr key={index}>
                        {Object.values(row).map((v, i) => (
                          <td key={i} className={typeof v === "number" ? "num mono" : ""}>
                            {v === null || v === undefined ? "—" : String(v)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {preview.warnings.map((w) => (
            <div key={w} className="banner banner--warning">
              {w}
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
